// invodiff-drawdown.jsx — Materials: the primary object. Each quoted material is
import { describeMatch } from './match-audit.jsx';
import { TradeSectionHeader } from './invodiff-jobs.jsx';
import React from 'react';
import { firebase, fbAuth, fbDb, fbStorage } from './firebase.js';
import {
  fmtUSD, fmtDelta, safeUrl, firestoreSafe, toCSV, downloadFile, relativeTime,
  invoiceDateOf, invoiceInRange, dateRangePresets, useFocusTrap,
  ROLES, ROLE_LABEL, ROLE_COLOR,
  usesSubInvoices, useJobsStore, useJobData, invoiceStore, deleteJobDeep, useVendorRules,
  normalizeUOM, isFootageUOM, stickFeet, itemFootage, convertToQuoteBasis, summarizeMatch,
  isAutoSku, itemKeyOf, levenshtein, fracToNum, normalizeForMatch, matchNormalize, matchLineItems,
  aggregateInvoices, compareInvoices, materialState, classifyRows, MATERIAL_STATE_META,
  getJobTrades, getSegments, lineTotal, subtotal, tax, grandTotal, pricesDiffer,
  statedInvoiceTotal, statedInvoiceTax, invoiceSpend,
  SAMPLE_VENDORS, INVOICE_A, INVOICE_B, SEED_VENDOR_RULES,
} from './invodiff-data.jsx';
import { Icon } from './invodiff-input.jsx';
import { RowSourceLinks, DocProvenanceModal } from './doc-provenance.jsx';
// a card showing RFQ qty → Purchased → Remaining (negative = over-ordered) next
// to Quoted/Actual cost and the variance, with ONE plain-English status
// (Good / Needs Review / Problem). Invoices are supporting evidence: expand a
// material to read its running-balance ledger (RFQ adds the baseline, each
// invoice subtracts) and — tucked away — how the lines were matched. Reads the
// same aggregateInvoices output as Summary/Issues, so the screens never disagree.

const fmtQty = (n) => (n == null ? '—' : (+n).toLocaleString(undefined, { maximumFractionDigits: 2 }));
// Unit/footage price — the headline of a quoted-vs-actual comparison. Shows up
// to 4 decimals so sub-cent contract rates aren't hidden.
const fmtPerUnit = (n) => (n == null || !(n > 0)
  ? '—'
  : '$' + (+n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }));

// Canonical material ↔ aliases. One material absorbs many vendor names/SKUs
// ("4\" 90 Elbow" billed as "4 STD ELBOW", "EL90-4", "BW ELBOW 4"). We surface
// the distinct invoice descriptions/SKUs that mapped to this canonical material
// but read differently from its quoted name — so the PM sees the other names a
// material is billed under without having to trust that vendors phrase it alike.
function aliasesOf(row) {
  const norm = (typeof normalizeForMatch === 'function')
    ? normalizeForMatch
    : ((s) => (s || '').toLowerCase().trim());
  const canonName = norm(row.desc);
  const canonSku  = (row.sku && row.sku !== '—') ? row.sku.trim().toUpperCase() : null;
  const names = [], nameSeen = new Set();
  const skus  = [], skuSeen  = new Set();
  for (const b of row.breakdown || []) {
    const src = b.srcItem;
    if (!src) continue;
    const desc = (src.desc || '').trim();
    if (desc) {
      const nd = norm(desc);
      if (nd && nd !== canonName && !nameSeen.has(nd)) { nameSeen.add(nd); names.push(desc); }
    }
    const sku = (src.sku || '').trim();
    if (sku) {
      const us = sku.toUpperCase();
      if (us !== canonSku && !skuSeen.has(us)) { skuSeen.add(us); skus.push(sku); }
    }
  }
  return { names, skus };
}

// Progress/remaining tone band (visual only — the headline status is materialState).
function ddTone(remaining, received) {
  if (remaining == null) return 'na';
  if (remaining < 0) return 'over';
  if (remaining === 0 && received > 0) return 'done';
  if (received > 0) return 'open';
  return 'idle';
}

function StatusPill({ state, sm }) {
  return (
    <span className={`mst mst-${state.level}${sm ? ' mst-sm' : ''}`} title={state.reasons?.join(' · ')}>
      <span className="mst-dot" />
      {state.label}
    </span>
  );
}

function MatMetric({ label, value, tone = 'default', strong }) {
  return (
    <div className={`matm matm-${tone}`}>
      <div className="matm-l">{label}</div>
      <div className={`matm-v num${strong ? ' is-strong' : ''}`}>{value}</div>
    </div>
  );
}

// Running-balance ledger — the literal model the PM asked for: the RFQ seeds the
// quantity, each invoice subtracts, and the closing balance is what's left
// (negative = over-ordered).
function MaterialLedger({ row }) {
  const uom = row.uomNorm || '';

  // Unit-mismatch rows can't be netted onto one basis — list the invoices plainly.
  if (row.flags.uom) {
    return (
      <div className="mled">
        <div className="mled-note">
          The quote and invoices use different units, so quantities can’t be combined into one balance.
        </div>
        {(row.breakdown || []).map((b) => (
          <div key={b.srcItem?._id} className="mled-row">
            <span className="mled-left">
              <span className="mled-src mono">{b.invoiceId}</span>
              <span className="mled-date">{b.date || '—'}</span>
              <span className="mled-at">@ {fmtUSD(b.unit)}</span>
            </span>
            <span className="mled-delta num">{fmtQty(b.srcQty ?? b.qty)} {b.srcUom || ''}</span>
            <span className="mled-bal num">{fmtUSD(b.lineTotal)}</span>
          </div>
        ))}
      </div>
    );
  }

  const baseline    = row.quotedQty;
  const hasBaseline = baseline != null;
  let bal = hasBaseline ? baseline : 0;

  return (
    <div className="mled">
      {hasBaseline ? (
        <div className="mled-row mled-rfq">
          <span className="mled-left"><span className="mled-label">RFQ baseline</span></span>
          <span className="mled-delta mled-add num">+{fmtQty(baseline)}</span>
          <span className="mled-bal num">{fmtQty(bal)} {uom}</span>
        </div>
      ) : (
        <div className="mled-row mled-rfq mled-norfq">
          <span className="mled-left"><span className="mled-label">No RFQ baseline — off-quote</span></span>
          <span className="mled-delta num">0</span>
          <span className="mled-bal num">0 {uom}</span>
        </div>
      )}

      {(row.breakdown || []).map((b) => {
        bal = +(bal - b.qty).toFixed(2);
        return (
          <div key={b.srcItem?._id} className="mled-row">
            <span className="mled-left">
              <span className="mled-src mono">{b.invoiceId}</span>
              <span className="mled-date">{b.date || '—'}</span>
              <span className="mled-at">@ {fmtUSD(b.unit)}</span>
            </span>
            <span className="mled-delta mled-sub num">−{fmtQty(b.qty)}</span>
            <span className={`mled-bal num${bal < 0 ? ' is-over' : ''}`}>{fmtQty(bal)} {uom}</span>
          </div>
        );
      })}

      <div className="mled-foot">
        <span className="mled-left"><span className="mled-label">Remaining</span></span>
        <span className="mled-delta" />
        <span className={`mled-rem num${bal < 0 ? ' is-over' : ''}`}>
          {fmtQty(bal)} {uom}{bal < 0 && <span className="mled-overtag">over by {fmtQty(Math.abs(bal))}</span>}
        </span>
      </div>
    </div>
  );
}

// Diagnostics the user shouldn't NEED — match method, confidence, alias, reasons —
// tucked behind a disclosure so the default card stays plain-English.
function MatchDetail({ row, state, aliases }) {
  const dm = (typeof describeMatch === 'function') ? describeMatch(row.matchType, row.matchScore) : { label: row.matchType, tier: 'review', detail: null };
  const confLabel = { high: 'high confidence', good: 'high confidence', review: 'needs review', none: '—' }[dm.tier] || dm.tier;
  const ak = aliases || { names: [], skus: [] };
  const hasAka = ak.names.length > 0 || ak.skus.length > 0;
  return (
    <details className="mdiag">
      <summary className="mdiag-sum">
        <Icon name="search" size={12} />
        <span>How this was matched</span>
        <Icon name="chev" size={11} className="mdiag-chev" />
      </summary>
      <div className="mdiag-body">
        <div className="mdiag-row">
          <span className="mdiag-k">Method</span>
          <span className="mdiag-v">{dm.label}{dm.detail ? ` · ${dm.detail}` : ''} <span className="mdiag-conf">({confLabel})</span></span>
        </div>
        {hasAka && (
          <div className="mdiag-row">
            <span className="mdiag-k">Also invoiced as</span>
            <span className="mdiag-v mdiag-aka">
              {ak.names.map((n, i) => <span key={'n' + i} className="aka-chip" title={n}>{n}</span>)}
              {ak.skus.map((s, i) => <span key={'s' + i} className="aka-chip aka-sku mono">{s}</span>)}
            </span>
          </div>
        )}
        {row.ruleId && (
          <div className="mdiag-row"><span className="mdiag-k">Link</span><span className="mdiag-v">Saved vendor rule</span></div>
        )}
        {row.matchNote && (
          <div className="mdiag-row"><span className="mdiag-k">Note</span><span className="mdiag-v">{row.matchNote}</span></div>
        )}
        {(state.reasons || []).length > 0 && (
          <div className="mdiag-row">
            <span className="mdiag-k">Why this status</span>
            <span className="mdiag-v">{state.reasons.join(' · ')}</span>
          </div>
        )}
      </div>
    </details>
  );
}

function MaterialCard({ row, state, expanded, onToggle, quote, invoices, onPreview }) {
  const uom        = row.uomNorm || '';
  const rfq        = row.quotedQty;
  const purchased  = row.invoicedQty || 0;
  const comparable = !row.flags.uom;
  const baseline   = rfq != null ? rfq : 0;
  const remaining  = comparable ? +(baseline - purchased).toFixed(2) : null;
  const over       = remaining != null && remaining < 0;
  const tone       = ddTone(remaining, purchased);
  const pct        = rfq > 0 ? Math.min(purchased / rfq, 1) : (purchased > 0 ? 1 : 0);

  // ── Unit price: the headline comparison ──────────────────────────────────
  // What the field actually pays per unit/foot vs. what was quoted — far more
  // actionable than the line totals (which also move with quantity).
  const qUnit      = row.quotedUnit;
  const aUnit      = row.invoicedAvgUnit;
  const unitOk     = comparable && qUnit > 0 && aUnit > 0;
  const uDelta     = unitOk ? +(aUnit - qUnit).toFixed(4) : null;
  const uPct       = unitOk ? (uDelta / qUnit) * 100 : null;
  const uTone      = !unitOk ? 'default' : uDelta > 0.0001 ? 'bad' : uDelta < -0.0001 ? 'ok' : 'default';
  const uDeltaStr  = !unitOk ? '—'
    : uDelta === 0 ? 'same'
    : `${uDelta > 0 ? '+' : '−'}${fmtPerUnit(Math.abs(uDelta))}${Math.abs(uPct) >= 0.5 ? ` (${uPct > 0 ? '+' : '−'}${Math.abs(uPct).toFixed(0)}%)` : ''}`;

  // Totals are kept as secondary context (de-emphasized below the metrics).
  const variance   = row.delta;
  const varPct     = comparable && row.quotedTotal > 0 ? (variance / row.quotedTotal) * 100 : null;
  const varTone    = !comparable ? 'default' : variance > 0.005 ? 'bad' : variance < -0.005 ? 'ok' : 'default';
  const varStr     = !comparable ? '—' : variance === 0 ? '$0' : (variance > 0 ? '+' : '−') + fmtUSD(Math.abs(variance));
  const varPctStr  = (varPct != null && Math.abs(varPct) >= 0.5) ? ` (${varPct > 0 ? '+' : ''}${varPct.toFixed(0)}%)` : '';

  // Purchased is labeled with the invoice's OWN unit when it differs from the
  // quote's, so a 1 EA line never reads as "1 LF".
  const purchasedUom = comparable ? uom : (row.invoicedUOM || '');

  const remTone = !comparable ? 'default' : over ? 'bad' : (remaining === 0 && purchased > 0) ? 'ok' : 'default';

  const aliases  = aliasesOf(row);
  const akaCount = aliases.names.length;

  return (
    <div className={`mat mat-${state.level}${expanded ? ' is-open' : ''}`}>
      <button className="mat-head" onClick={onToggle} aria-expanded={expanded}>
        <Icon name="chev" size={13} className="mat-chev" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }} />
        <span className="mat-id">
          <span className="mat-name" title={row.desc}>{row.desc}</span>
          {row.sku && row.sku !== '—' && <span className="mat-sku mono">{row.sku}</span>}
          {akaCount > 0 && (
            <span className="mat-aka" title={`Also invoiced as: ${aliases.names.join(' · ')}`}>
              +{akaCount} {akaCount === 1 ? 'name' : 'names'}
            </span>
          )}
        </span>
        <StatusPill state={state} />
      </button>

      <div className="mat-metrics">
        <MatMetric label={`Quoted /${uom || 'unit'}`} value={fmtPerUnit(qUnit)} />
        <MatMetric label={`Actual /${uom || 'unit'}`} value={fmtPerUnit(aUnit)} />
        <MatMetric label="Unit price Δ" tone={uTone} strong value={uDeltaStr} />
        <MatMetric label="RFQ qty"    value={rfq != null ? `${fmtQty(rfq)} ${uom}` : '—'} />
        <MatMetric label="Purchased"  value={`${fmtQty(purchased)} ${purchasedUom}`} />
        <MatMetric label="Remaining"  value={comparable ? `${fmtQty(remaining)} ${uom}` : 'unit mismatch'} tone={remTone} />
      </div>

      {/* Line totals — secondary context (they also move with quantity). */}
      <div className="mat-totals">
        <span className="mat-totals-item">Quoted total <span className="num">{fmtUSD(row.quotedTotal)}</span></span>
        <span className="mat-totals-item">Actual total <span className="num">{fmtUSD(row.invoicedTotal)}</span></span>
        <span className={`mat-totals-item mat-totals-var matm-${varTone}`}>Δ <span className="num">{varStr}{varPctStr}</span></span>
      </div>

      <div className="mat-prog">
        <div className="mat-bar"><div className={`mat-bar-fill mat-bar-${tone}`} style={{ width: `${pct * 100}%` }} /></div>
        {over ? <span className="mat-prog-tag mat-prog-over">over-pulled</span>
          : tone === 'done' ? <span className="mat-prog-tag mat-prog-done">fully received</span>
          : <span className="mat-prog-pct num">{Math.round(pct * 100)}% received</span>}
      </div>

      {expanded && (
        <div className="mat-detail">
          <div className="mat-detail-hd">Invoice ledger</div>
          <MaterialLedger row={row} />
          <RowSourceLinks row={row} quote={quote} invoices={invoices} onPreview={onPreview} />
          <MatchDetail row={row} state={state} aliases={aliases} />
        </div>
      )}
    </div>
  );
}

function DrawdownKpi({ label, value, sub, tone = 'default' }) {
  return (
    <div className={`ddk ddk-${tone}`}>
      <div className="ddk-label">{label}</div>
      <div className="ddk-value num">{value}</div>
      {sub && <div className="ddk-sub">{sub}</div>}
    </div>
  );
}

// One trade's drawdown: KPIs, status filters, quoted materials, and off-quote
// charges. `quote`/`invoices` are already scoped to this trade by getSegments;
// it owns its own filter + expand state so each trade section is independent.
function DrawdownSection({ trade, showHeader, quote, invoices, vendorRules, tolerances, taxExempt }) {
  const [filter, setFilter]     = React.useState('all'); // all | problem | review | good
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [preview, setPreview]   = React.useState(null); // { doc, sel } for the popup PDF viewer
  const openPreview = React.useCallback((doc, sel) => setPreview({ doc, sel }), []);

  const rules = React.useMemo(
    () => (vendorRules || {})[quote?.vendor || ''] || [],
    [vendorRules, quote?.vendor]
  );

  const result = React.useMemo(
    () => quote && invoices.length > 0 ? aggregateInvoices(quote, invoices, rules, { taxExempt }) : null,
    [quote, invoices, rules, taxExempt]
  );

  // Canonical split + classification (same source the Issues page + dashboard
  // use): quoted materials vs. off-quote charges, with one status each. Counts
  // and statuses therefore always reconcile across screens.
  const classified = React.useMemo(
    () => result ? classifyRows(result.rows, tolerances) : null,
    [result, tolerances]
  );
  const materials  = classified ? classified.materials  : [];
  const newCharges = classified ? classified.newCharges : [];
  const stateOf    = classified ? classified.states     : new Map();
  const counts     = classified ? classified.count       : { good: 0, review: 0, problem: 0 };

  const matchFilter = (r) => filter === 'all' || stateOf.get(r).level === filter;
  const shownMaterials = React.useMemo(() => materials.filter(matchFilter),  [materials, filter, stateOf]);
  const shownCharges   = React.useMemo(() => newCharges.filter(matchFilter), [newCharges, filter, stateOf]);

  // Stable, unique key per real row (position-based) — distinct rows can share a
  // SKU + description, so a SKU/desc key collided and broke expand state + keys.
  const keyMap = React.useMemo(() => {
    const m = new Map();
    (result ? result.rows : []).forEach((r, i) => {
      if (!r.synthetic) m.set(r, `${r.flags.added ? 'oq' : 'm'}-${i}-${(r.sku && r.sku !== '—') ? r.sku : 'na'}`);
    });
    return m;
  }, [result]);

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const header = showHeader ? (
    <TradeSectionHeader trade={trade}
      sub={result ? `${fmtUSD(result.summary.invoicedTotal)} of ${fmtUSD(result.summary.quotedTotal)}` : null} />
  ) : null;

  // Per-trade empty states (only reachable on a multi-trade job — the single
  // path is gated in the container so its full-page states are preserved).
  if (!quote) {
    return <>{header}<div className="dd-empty">No quote loaded for {trade}.</div></>;
  }
  if (invoices.length === 0) {
    return <>{header}<div className="dd-empty">No invoices filed under {trade} yet.</div></>;
  }

  const remainingBudget = +(result.summary.quotedTotal - result.summary.invoicedTotal).toFixed(2);
  const totalRows = materials.length + newCharges.length;
  const FILTERS = [
    ['all',     'All',           totalRows],
    ['problem', 'Problem',       counts.problem],
    ['review',  'Needs review',  counts.review],
    ['good',    'Good',          counts.good],
  ];

  const keyOf = (r) => keyMap.get(r);

  return (
    <>
      {header}
      <div className="dd-kpis">
        <DrawdownKpi label="Quoted" value={fmtUSD(result.summary.quotedTotal)} sub={`${materials.length} quoted material${materials.length !== 1 ? 's' : ''}`} />
        <DrawdownKpi label="Invoiced" value={fmtUSD(result.summary.invoicedTotal)} sub={`${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`} tone="info" />
        <DrawdownKpi label="Remaining budget" value={fmtUSD(remainingBudget)} sub={remainingBudget < 0 ? 'over budget' : 'left to spend'} tone={remainingBudget < 0 ? 'bad' : 'ok'} />
        <DrawdownKpi label="Problems" value={counts.problem} sub={counts.review > 0 ? `${counts.review} more need review` : counts.problem === 0 ? 'all on track' : 'need a decision'} tone={counts.problem > 0 ? 'bad' : counts.review > 0 ? 'warn' : 'ok'} />
      </div>

      <div className="dd-filters">
        {FILTERS.map(([key, label, count]) => (
          <button key={key} className={`dd-filter${filter === key ? ' is-on' : ''}`} onClick={() => setFilter(key)}>
            {label}<span className="dd-filter-count num">{count}</span>
          </button>
        ))}
      </div>

      {/* Quoted materials — the RFQ lines, the primary object of this screen. */}
      <div className="dd-list">
        {shownMaterials.map((r) => {
          const key = keyOf(r);
          return (
            <MaterialCard
              key={key} row={r} state={stateOf.get(r)}
              expanded={expanded.has(key)} onToggle={() => toggle(key)}
              quote={quote} invoices={invoices} onPreview={openPreview}
            />
          );
        })}
        {shownMaterials.length === 0 && newCharges.length === 0 && (
          <div className="dd-empty">No materials match this filter.</div>
        )}
      </div>

      {/* Off-quote charges — billed but NOT on the RFQ. Kept separate so the
          material counts above never fold in lines that were never quoted. */}
      {newCharges.length > 0 && (
        <div className="dd-offquote">
          <div className="dd-offquote-hd">
            <Icon name="flag" size={13} />
            <span className="dd-offquote-t">Off-quote charges</span>
            <span className="dd-offquote-c num">{newCharges.length}</span>
            <span className="dd-offquote-note">Billed but not on the RFQ — resolve these on the Issues tab.</span>
          </div>
          <div className="dd-list">
            {shownCharges.map((r) => {
              const key = keyOf(r);
              return (
                <MaterialCard
                  key={key} row={r} state={stateOf.get(r)}
                  expanded={expanded.has(key)} onToggle={() => toggle(key)}
                  quote={quote} invoices={invoices} onPreview={openPreview}
                />
              );
            })}
            {shownCharges.length === 0 && <div className="dd-empty">No off-quote charges match this filter.</div>}
          </div>
        </div>
      )}

      {preview && (
        <DocProvenanceModal invoice={preview.doc} initialSel={preview.sel}
          onClose={() => setPreview(null)} />
      )}
    </>
  );
}

function DrawdownView({ job, userId, vendorRules, selectedTrades }) {
  const [quote, setQuote]           = React.useState(null);
  const [quotesByTrade, setQuotesByTrade] = React.useState({});
  const [trades, setTrades]         = React.useState(() => getJobTrades(job));
  const [invoices, setInvoices]     = React.useState([]);
  const [tolerances, setTols]       = React.useState(null);
  const [taxExempt, setTaxExempt]   = React.useState(!!job.taxExempt);
  const [synced, setSynced]         = React.useState(false);

  const isSample = job.id === '_sample';

  // Live job doc + invoices via the shared dual-shape hook (invodiff-data.jsx).
  const live = useJobData(isSample ? null : job.id);
  React.useEffect(() => {
    if (isSample) {
      setQuote(INVOICE_A); setInvoices([INVOICE_B]); setSynced(true);
      return;
    }
    if (!live.synced) { setSynced(false); return; }
    if (live.exists) {
      const d = live.data;
      setQuote(d.quote ?? null);
      setQuotesByTrade(d.quotesByTrade ?? {});
      setTrades(getJobTrades(d));
      setInvoices(live.invoices.filter((inv) => !inv._hidden));
      setTols(d.tolerances || null);
      setTaxExempt(!!d.taxExempt);
    }
    setSynced(true);
  }, [isSample, live]);

  const multiTrade = trades.length > 1;
  const segments = React.useMemo(
    () => getSegments({ quote, invoices, quotesByTrade, trades }, selectedTrades),
    [quote, invoices, quotesByTrade, trades, selectedTrades]
  );
  const anyQuote = segments.some((s) => s.quote);
  const anyInv   = segments.some((s) => s.invoices.length > 0);

  if (!synced) {
    return <div className="dd-wrap"><div className="dd-loading"><div className="dd-spin" /></div><DrawdownStyles /></div>;
  }
  if (!anyQuote) {
    return (
      <div className="dd-wrap">
        <div className="dd-state">
          <div className="dd-state-icon"><Icon name="layers" size={28} /></div>
          <div className="dd-state-h">No quote loaded</div>
          <div className="dd-state-sub">Upload an RFQ/quote in the Invoices tab — every quoted material is tracked from there.</div>
        </div>
        <DrawdownStyles />
      </div>
    );
  }
  if (!anyInv) {
    return (
      <div className="dd-wrap">
        <div className="dd-state">
          <div className="dd-state-icon"><Icon name="file" size={28} /></div>
          <div className="dd-state-h">No invoices yet</div>
          <div className="dd-state-sub">Add invoices in the Invoices tab — each one draws down its material here.</div>
        </div>
        <DrawdownStyles />
      </div>
    );
  }

  return (
    <div className="dd-wrap">
      <div className="dd-hero">
        <div className="dd-hero-tag mono">JOB {job.jobNumber} · MATERIALS</div>
        <h1 className="dd-hero-h">Materials</h1>
        <p className="dd-hero-sub">
          Every quoted material and how far it's been drawn down. Remaining counts down from the RFQ as
          invoices arrive — a negative <span className="dd-neg-chip">remaining</span> means more was billed
          than quoted. Expand a material to see its invoice ledger.
        </p>
      </div>

      {segments.map((seg) => (
        <DrawdownSection
          key={`${job.id}:${seg.trade || '_single'}`} trade={seg.trade} showHeader={multiTrade}
          quote={seg.quote} invoices={seg.invoices}
          vendorRules={vendorRules} tolerances={tolerances} taxExempt={taxExempt}
        />
      ))}

      <DrawdownStyles />
    </div>
  );
}

const DrawdownStyles = () => (
  <style>{`
    .dd-wrap { width: 100%; max-width: var(--app-max); margin: 0 auto; padding: 40px var(--app-pad) 72px; display: flex; flex-direction: column; gap: 18px; }

    .dd-loading { display: flex; align-items: center; justify-content: center; min-height: 50vh; }
    .dd-spin { width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid var(--border); border-top-color: var(--accent); animation: dz-spin .7s linear infinite; }
    .dd-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; min-height: 40vh; text-align: center; }
    .dd-state-icon { color: var(--fg-dim); opacity: 0.4; }
    .dd-state-h { font-size: 15px; font-weight: 600; color: var(--fg-muted); }
    .dd-state-sub { font-size: 13px; color: var(--fg-dim); max-width: 360px; line-height: 1.55; }
    .dd-empty { padding: 32px; text-align: center; color: var(--fg-dim); font-size: 13px; }

    /* ── Off-quote charges section (kept distinct from quoted materials) ── */
    .dd-offquote { display: flex; flex-direction: column; gap: 10px; margin-top: 10px;
      border-top: 1px dashed var(--border-strong); padding-top: 18px; }
    .dd-offquote-hd { display: flex; align-items: center; gap: 9px; }
    .dd-offquote-hd > svg { color: var(--bad); }
    .dd-offquote-t { font-size: 13.5px; font-weight: 700; letter-spacing: -0.01em; }
    .dd-offquote-c { font-size: 11px; font-weight: 600; padding: 1px 8px; border-radius: 999px; background: var(--bad-bg); color: var(--bad); }
    .dd-offquote-note { font-size: 11.5px; color: var(--fg-dim); }

    /* ── Hero ────────────────────────────────────────────────────── */
    .dd-hero-tag { font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 6px; }
    .dd-hero-h   { font-size: 25px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 6px; }
    .dd-hero-sub { font-size: 14.5px; color: var(--fg-muted); margin: 0; line-height: 1.6; max-width: 640px; }
    .dd-neg-chip { font-family: "IBM Plex Mono", monospace; font-size: 11.5px; color: var(--bad); background: var(--bad-bg);
      border: 1px solid oklch(from var(--bad) l calc(c*.5) h / .35); border-radius: 4px; padding: 0 6px; margin: 0 4px; }

    /* ── KPI cards ───────────────────────────────────────────────── */
    .dd-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
    .ddk { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); padding: 14px 16px; box-shadow: var(--shadow-1); border-top: 2px solid var(--border-strong); }
    .ddk-info { border-top-color: var(--accent); }
    .ddk-ok   { border-top-color: var(--ok); }
    .ddk-bad  { border-top-color: var(--bad); }
    .ddk-label { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--fg-dim); font-weight: 600; }
    .ddk-value { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin-top: 4px; }
    .ddk-bad .ddk-value { color: var(--bad); }
    .ddk-sub { font-size: 11.5px; color: var(--fg-dim); margin-top: 2px; }

    /* ── Filters ─────────────────────────────────────────────────── */
    .dd-filters { display: flex; gap: 6px; flex-wrap: wrap; }
    .dd-filter { appearance: none; border: 1px solid var(--border); background: var(--surface); color: var(--fg-muted);
      padding: 5px 12px; border-radius: 999px; font: inherit; font-size: 12px; cursor: pointer;
      display: inline-flex; align-items: center; gap: 6px; transition: color .12s, background .12s, border-color .12s; }
    .dd-filter:hover:not(.is-on) { color: var(--fg); }
    .dd-filter.is-on { background: oklch(from var(--accent) l c h / 0.14); color: var(--accent); border-color: oklch(from var(--accent) l calc(c*.5) h / .4); font-weight: 600; }
    .dd-filter-count { font-size: 10.5px; background: var(--surface-3); color: var(--fg-dim); padding: 1px 6px; border-radius: 999px; }
    .dd-filter.is-on .dd-filter-count { background: oklch(from var(--accent) l c h / 0.22); color: var(--accent); }

    /* ── Status pill (Good / Needs Review / Problem) ─────────────── */
    .mst { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
      height: 24px; padding: 0 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600;
      border: 1px solid transparent; white-space: nowrap; }
    .mst-sm { height: 20px; padding: 0 8px; font-size: 10.5px; }
    .mst-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
    .mst-good    { background: var(--ok-bg);   color: var(--ok);   border-color: oklch(from var(--ok)   l calc(c*.5) h / .35); }
    .mst-review  { background: var(--warn-bg); color: var(--warn); border-color: oklch(from var(--warn) l calc(c*.5) h / .4); }
    .mst-problem { background: var(--bad-bg);  color: var(--bad);  border-color: oklch(from var(--bad)  l calc(c*.5) h / .4); }

    /* ── Material cards ──────────────────────────────────────────── */
    .dd-list { display: flex; flex-direction: column; gap: 10px; }
    .mat { background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg);
      box-shadow: var(--shadow-1); overflow: hidden; position: relative; }
    .mat::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: transparent; }
    .mat-problem::before { background: var(--bad); }
    .mat-review::before  { background: var(--warn); }
    .mat-good::before    { background: oklch(from var(--ok) l c h / 0.5); }

    .mat-head { appearance: none; width: 100%; background: transparent; border: 0; cursor: pointer;
      display: flex; align-items: center; gap: 10px; padding: 13px 16px 13px 18px; font: inherit; text-align: left;
      transition: background .12s; }
    .mat-head:hover { background: var(--surface-2); }
    .mat-chev { color: var(--fg-dim); flex-shrink: 0; transition: transform .12s; }
    .mat-id { display: flex; align-items: baseline; gap: 9px; min-width: 0; flex: 1; }
    .mat-name { font-size: 14.5px; font-weight: 600; letter-spacing: -0.01em; color: var(--fg);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .mat-sku { font-size: 10.5px; color: var(--fg-dim); background: var(--surface-3); border: 1px solid var(--border);
      padding: 1px 6px; border-radius: 4px; flex-shrink: 0; }
    /* "+N names" — the material is billed under other vendor descriptions too. */
    .mat-aka { font-size: 10px; color: var(--fg-dim); flex-shrink: 0; cursor: help;
      display: inline-flex; align-items: center; gap: 3px; }
    .mat-aka::before { content: '⇄'; font-size: 10px; opacity: 0.7; }

    /* Metric strip — the six numbers the PM reads at a glance. */
    .mat-metrics { display: grid; grid-template-columns: repeat(6, 1fr); gap: 1px;
      background: var(--border); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
    .matm { background: var(--surface); padding: 10px 14px; display: flex; flex-direction: column; gap: 3px; }
    .matm-l { font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--fg-dim); font-weight: 600; }
    .matm-v { font-size: 14px; font-weight: 600; color: var(--fg); letter-spacing: -0.01em; }
    .matm-v.is-strong { font-size: 16px; font-weight: 700; }
    .matm-ok  .matm-v { color: var(--ok); }
    .matm-bad .matm-v { color: var(--bad); }
    .matm-pct { font-size: 11px; font-weight: 500; opacity: 0.8; margin-left: 3px; }

    .mat-totals { display: flex; flex-wrap: wrap; gap: 6px 18px; padding: 8px 16px 0 18px; font-size: 11.5px; color: var(--fg-dim); }
    .mat-totals-item .num { color: var(--fg-muted); font-weight: 600; margin-left: 3px; }
    .mat-totals-var.matm-bad .num { color: var(--bad); }
    .mat-totals-var.matm-ok  .num { color: var(--ok); }

    .mat-prog { display: flex; align-items: center; gap: 10px; padding: 11px 16px 12px 18px; }
    .mat-bar { flex: 1; height: 6px; border-radius: 999px; background: var(--surface-3); overflow: hidden; }
    .mat-bar-fill { height: 100%; border-radius: 999px; background: var(--accent); transition: width .2s; }
    .mat-bar-done { background: var(--ok); }
    .mat-bar-over { background: var(--bad); }
    .mat-bar-idle { background: var(--border-strong); }
    .mat-bar-na   { background: var(--warn); }
    .mat-prog-pct { font-size: 11px; color: var(--fg-dim); flex-shrink: 0; }
    .mat-prog-tag { font-size: 9.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
      padding: 2px 7px; border-radius: 4px; flex-shrink: 0; }
    .mat-prog-over { color: var(--bad); background: var(--bad-bg); }
    .mat-prog-done { color: var(--ok); background: var(--ok-bg); }

    /* ── Expanded: ledger + match diagnostics ────────────────────── */
    .mat-detail { background: var(--surface-2); border-top: 1px solid var(--border); padding: 14px 18px 16px; }
    .mat-detail-hd { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--fg-dim); font-weight: 600; margin-bottom: 8px; }

    .mled { display: flex; flex-direction: column; font-family: "IBM Plex Mono", monospace; }
    .mled-note { font-family: "IBM Plex Sans", sans-serif; font-size: 12px; color: var(--fg-muted); margin-bottom: 8px; line-height: 1.5; }
    .mled-row { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 14px;
      padding: 6px 10px; border-radius: var(--r-sm); font-size: 12px; }
    .mled-row:hover { background: var(--surface-3); }
    .mled-left { display: flex; align-items: baseline; gap: 10px; min-width: 0; font-family: "IBM Plex Mono", monospace; }
    .mled-label { font-family: "IBM Plex Sans", sans-serif; font-weight: 600; color: var(--fg); }
    .mled-src { color: var(--accent); font-size: 11px; }
    .mled-date { color: var(--fg-dim); font-size: 11px; }
    .mled-at { color: var(--fg-dim); font-size: 11px; }
    .mled-delta { text-align: right; font-variant-numeric: tabular-nums; min-width: 70px; color: var(--fg-muted); }
    .mled-add { color: var(--ok); font-weight: 600; }
    .mled-sub { color: var(--fg); }
    .mled-bal { text-align: right; font-variant-numeric: tabular-nums; min-width: 96px; color: var(--fg); font-weight: 500; }
    .mled-bal.is-over { color: var(--bad); }
    .mled-rfq { border-bottom: 1px dashed var(--border); }
    .mled-norfq .mled-label { color: var(--fg-dim); font-style: italic; }
    .mled-foot { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 14px;
      margin-top: 4px; padding: 9px 10px 2px; border-top: 1px solid var(--border-strong); }
    .mled-rem { justify-self: end; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; color: var(--fg);
      display: inline-flex; align-items: baseline; gap: 8px; }
    .mled-rem.is-over { color: var(--bad); }
    .mled-overtag { font-family: "IBM Plex Sans", sans-serif; font-size: 10px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.04em; color: var(--bad); background: var(--bad-bg); padding: 1px 6px; border-radius: 4px; }

    /* Diagnostics disclosure — confidence / fuzzy / alias live here, not up front. */
    .mdiag { margin-top: 12px; border-top: 1px solid var(--border); padding-top: 8px; }
    .mdiag-sum { display: inline-flex; align-items: center; gap: 7px; cursor: pointer; list-style: none;
      font-size: 11.5px; font-weight: 600; color: var(--fg-dim); user-select: none; }
    .mdiag-sum::-webkit-details-marker { display: none; }
    .mdiag-sum:hover { color: var(--fg-muted); }
    .mdiag-sum > svg:first-child { color: var(--fg-dim); }
    .mdiag-chev { transition: transform .15s; }
    .mdiag[open] .mdiag-chev { transform: rotate(90deg); }
    .mdiag-body { margin-top: 8px; display: flex; flex-direction: column; gap: 5px; }
    .mdiag-row { display: grid; grid-template-columns: 110px 1fr; gap: 10px; font-size: 12px; line-height: 1.45; }
    .mdiag-k { color: var(--fg-dim); font-weight: 500; }
    .mdiag-v { color: var(--fg-muted); }
    .mdiag-conf { color: var(--fg-dim); }
    /* Alias chips — the distinct vendor names/SKUs absorbed into this material. */
    .mdiag-aka { display: flex; flex-wrap: wrap; gap: 5px; }
    .aka-chip { font-size: 11px; color: var(--fg-muted); background: var(--surface-3); border: 1px solid var(--border);
      padding: 1px 7px; border-radius: 4px; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .aka-sku { color: var(--fg-dim); font-size: 10.5px; }

    @keyframes dz-spin { to { transform: rotate(360deg); } }

    @media (max-width: 1000px) { .dd-kpis { grid-template-columns: repeat(2, 1fr); } }
    @media (max-width: 760px) {
      .dd-wrap { padding: 28px 16px 48px; }
      /* Six metrics reflow to a 2-up grid so each stays readable on a phone. */
      .mat-metrics { grid-template-columns: repeat(2, 1fr); }
      .mled-row, .mled-foot { grid-template-columns: 1fr auto; }
      .mled-delta { display: none; }
      .mdiag-row { grid-template-columns: 1fr; gap: 1px; }
    }
  `}</style>
);

export { DrawdownView };
